import React, { useContext, useState } from "react"
import { graphql } from "gatsby"
import { StaticImage } from "gatsby-plugin-image"
import get from "lodash.get"
import { LocaleContext } from "contexts"
import { siteCountry } from "utils/env"
import {
  formatArticles,
  formatQuestionPairs,
  formatCases,
  formatTestimonial,
} from "utils/prismic"
import useTranslations from "hooks/useTranslations"
import SEO from "components/seo"
import HowItWorks from "components/organisms/HowItWorks"
import LogoCloudAnimated from "components/organisms/LogoCloudAnimated"
import FAQ from "components/organisms/somebuddy/FAQ"
import ListHorizontal from "components/organisms/somebuddy/ListHorizontal"
import Banner from "components/molecules/Banner"
import StatsGirl from "components/organisms/schools/StatsGirl"
import SubscribeForm from "components/SubscribeForm"
import Contact from "components/organisms/somebuddy/Contact"
import Hero from "components/organisms/webinar/Hero"
import i18nConfig from "../../config/i18n"
import BlogPosts from "components/organisms/common/BlogPosts"
import Button from "components/buttons/Button"
import Segments from "components/organisms/main/ProtectedSegments"
import "styles/index.global.css"
import Stats from "components/organisms/common/Stats"
import Testimonials from "components/organisms/somebuddy/Testimonials"
// import QuotesCarousel from "components/variants/QuotesCarouselMain"
import Report2025ReferenceSection from "components/organisms/common/Report2025ReferenceSection"

const HeroButton = () => {
  const { buttonLogin } = useTranslations()
  const appLink = process.env.GATSBY_TOOSA_URL
  return <Button bgStyle="light" label={buttonLogin} link={appLink} outbound />
}

const HeroImage = () => (
  <StaticImage src="../images/schools/istockphoto-471947429.jpeg" alt="" />
)

const FaqImage = () => (
  <StaticImage src="../images/schools/AdobeStock_195624071.jpeg" alt="" />
)

const IndexPage = ({ data, pageContext: { currentPage } }) => {
  const {
    coverTitle,
    coverSubTitle1,
    companyFact1Large,
    companyFact1Small,
    companyFact2Large,
    companyFact2Small,
    companyFact3Large,
    companyFact3Small,
    companyFact4Large,
    companyFact4Small,
    companyFact5Large,
    companyFact5Small,
    companyFact6Large,
    companyFact6Small,
    quotesTitle,
  } = useTranslations()
  const { locale } = useContext(LocaleContext)
  const countryOptions = i18nConfig[siteCountry]
  const localeOptions = countryOptions[locale]
  const [showBanner, setShowBanner] = useState(get(localeOptions, "showBanner"))

  const showNewsletterSubscription = get(
    localeOptions,
    "showNewsletterSubscription",
  )

  const prismicArticles =
    siteCountry === "fi"
      ? get(data, "filteredPrismicNewsArticles", { edges: [] })
      : get(data, "latestPrismicNewsArticles", { edges: [] })
  const articles = formatArticles(prismicArticles)

  const prismicFAQ =
    data && data.allPrismicFaq ? data.allPrismicFaq : { edges: [] }
  const questionAnswerPairs = formatQuestionPairs(prismicFAQ)

  const testimonials = data.allPrismicTestimonial.nodes.map((t) =>
    formatTestimonial({ id: t.uid, ...t.data }),
  )

  const cases = formatCases(get(data, "allPrismicCase", []))

  const stats = [
    [
      {
        line1: companyFact1Large,
        line2: companyFact1Small,
      },
      {
        line1: companyFact5Large,
        line2: companyFact5Small,
      },
      {
        line1: companyFact3Large,
        line2: companyFact3Small,
      },
    ],
    [
      {
        line1: companyFact6Large,
        line2: companyFact6Small,
      },
      {
        line1: companyFact4Large,
        line2: companyFact4Small,
      },
      {
        line1: companyFact2Large,
        line2: companyFact2Small,
      },
    ],
  ]

  return (
    <>
      <SEO title={coverTitle} />
      <div className="sections">
        <Hero
          title={coverTitle}
          description={coverSubTitle1}
          descriptionBelow
          bgColor="green"
          Image={HeroImage}
          video="/hero-video.mp4"
          showButton
          HeroButton={HeroButton}
          poster="/hero-poster-small.webp"
        />
        <Stats facts={stats[0]} />
        <StatsGirl />
        <Stats facts={stats[1]} theme="green" />
        {siteCountry === "fi" || siteCountry === "global" ? (
          <Report2025ReferenceSection />
        ) : null}
        <Segments />
        <LogoCloudAnimated center currentPage={currentPage} rows={2} />
        <HowItWorks />
        <BlogPosts />
        {showNewsletterSubscription ? <SubscribeForm /> : null}
        <Testimonials
          list={testimonials}
          bgColor={locale === "sv-se" ? "light" : "black"}
        />
        <ListHorizontal
          items={articles}
          allItemsLink="/media"
          theme="green-yellow"
        />
        <FAQ pairs={questionAnswerPairs} Image={FaqImage} />
        <Contact />

        {showBanner ? (
          <Banner handleClose={() => setShowBanner(false)} />
        ) : null}
      </div>
    </>
  )
}

export default IndexPage

export const pageQuery = graphql`
  query ($locale: String!, $currentPage: String!) {
    filteredPrismicNewsArticles: allPrismicNewsArticle(
      sort: { data: { published_at: DESC } }
      limit: 3
      filter: {
        lang: { eq: $locale }
        data: { show_on_pages: { elemMatch: { page: { eq: $currentPage } } } }
      }
    ) {
      edges {
        node {
          id
          data {
            article {
              text
            }
            published_at(formatString: "MMMM DD, YYYY")
            link_to_article {
              url
            }
            publisher_logo {
              url
            }
            publisher_name {
              text
            }
          }
        }
      }
    }
    latestPrismicNewsArticles: allPrismicNewsArticle(
      sort: { data: { published_at: DESC } }
      limit: 3
      filter: { lang: { eq: $locale } }
    ) {
      edges {
        node {
          id
          data {
            article {
              text
            }
            published_at(formatString: "MMMM DD, YYYY")
            link_to_article {
              url
            }
            publisher_logo {
              url
            }
            publisher_name {
              text
            }
          }
        }
      }
    }
    allPrismicFaq(
      sort: { data: { index: ASC } }
      filter: {
        lang: { eq: $locale }
        data: { show_on_pages: { elemMatch: { page: { eq: $currentPage } } } }
      }
    ) {
      edges {
        node {
          data {
            index
            question {
              text
            }
            answer {
              richText
            }
          }
        }
      }
    }
    allPrismicCase(
      limit: 3
      filter: {
        lang: { eq: $locale }
        data: { show_on_pages: { elemMatch: { page: { eq: $currentPage } } } }
      }
    ) {
      nodes {
        lang
        id
        data {
          content {
            richText
          }
          description {
            text
          }
          quote {
            text
          }
          title {
            text
          }
          avatar {
            gatsbyImageData(width: 144)
          }
          index
          slug
          tag
        }
      }
    }
    allPrismicTestimonial(
      limit: 2
      filter: {
        lang: { eq: $locale }
        data: { show_on_pages: { elemMatch: { page: { eq: $currentPage } } } }
      }
    ) {
      nodes {
        id
        data {
          fact_large {
            text
          }
          fact_small {
            text
          }
          logo {
            url
            alt
          }
          title {
            text
          }
        }
        uid
      }
    }
  }
`
